import React, { useState, useEffect, useRef } from 'react';
import { Newspaper, ExternalLink, TrendingUp, AlertCircle, Loader2, Clock, Globe, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import DashboardLayout from '../components/DashboardLayout';
import { financialDataAPI } from '../services/apiServices';
import { toast } from '../hooks/use-toast';

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(245, 158, 11, 0.08)", ...props }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        backgroundImage: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

const News = () => {
  const [news, setNews] = useState(() => {
    const cached = localStorage.getItem('finassure_cached_news');
    return cached ? JSON.parse(cached) : [];
  });
  const [loading, setLoading] = useState(false); // Default false as we might have cache
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only fetch if no cache exists
    if (news.length === 0) {
      fetchNews(false);
    }
  }, []);

  const fetchNews = async (isManual = true) => {
    try {
      setLoading(true);
      setError(null);
      const data = await financialDataAPI.getNews();
      if (data && data.length > 0) {
        setNews(data);
        localStorage.setItem('finassure_cached_news', JSON.stringify(data));
        localStorage.setItem('finassure_news_timestamp', new Date().getTime());

        // Only show toast on manual sync to avoid annoying popups
        if (isManual) {
          toast({
            title: 'Intelligence Synchronized',
            description: `Aggregated ${data.length} high-frequency headlines.`,
            variant: "default"
          });
        }
      } else {
        setError('Market communications interrupted. Checking neural link...');
      }
    } catch (err) {
      setError(`Sync Failure: ${err.message}`);
      if (isManual) {
        toast({ title: 'Sync Failed', description: err.message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewsClick = (url) => {
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const breakingNews = news.length > 0 ? news[0] : null;
  const otherNews = news.length > 1 ? news.slice(1) : [];

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-950 p-4 md:p-8 font-inter selection:bg-white/10 overflow-x-hidden text-[#F8FAFC]">

        {/* === CUSTOM STYLES === */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          .font-inter { font-family: 'Inter', sans-serif; }
          
          .gold-shiny-border {
            border: 0.5px solid rgba(245, 158, 11, 0.2);
            transition: all 0.3s ease;
          }
          
          .gold-shiny-border:hover {
            border-color: rgba(245, 158, 11, 0.5);
            box-shadow: 0 4px 20px rgba(245, 158, 11, 0.1);
          }

          .glass-card {
            background: #000000;
            border: 1px solid rgba(255, 255, 255, 0.05);
          }

          .preserve-3d { transform-style: preserve-3d; }
          
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>

        <div className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="animate-in fade-in slide-in-from-left duration-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] text-[8px] font-black uppercase tracking-[0.3em] rounded-full border border-[#F59E0B]/20">
                Daily Intelligence
              </span>
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight uppercase">
              Financial <span className="text-[#F59E0B] drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]">News</span>
            </h1>
            <p className="text-[#94A3B8] text-xs mt-2 font-medium max-w-xl leading-relaxed italic opacity-80">
              "Curated high-frequency updates from global liquidity hubs and central bank corridors."
            </p>
          </div>
          <button
            onClick={() => fetchNews(true)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/10 rounded-xl text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white hover:border-white/40 transition-all active:scale-95"
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <TrendingUp size={14} />}
            Sync Headlines
          </button>
        </div>

        {loading && news.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-[60vh] gap-4">
            <div className="relative">
              <div className="w-16 h-16 border-t-2 border-r-2 border-white/20 rounded-full animate-spin"></div>
              <Newspaper className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/40" size={24} />
            </div>
            <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Initializing Data Stream</p>
          </div>
        ) : error && news.length === 0 ? (
          <div className="text-center py-20 bg-black rounded-[2rem] border border-red-500/20 glass-card">
            <AlertCircle className="mx-auto text-red-400 mb-4 animate-bounce" size={48} />
            <p className="text-red-400 font-bold uppercase tracking-widest text-sm mb-2">Neural Link Lost</p>
            <p className="text-slate-500 text-xs font-medium">{error}</p>
            <button onClick={fetchNews} className="mt-6 px-6 py-2 bg-slate-800 rounded-lg text-xs font-bold text-white hover:bg-slate-700 transition-all uppercase tracking-widest">Execute Reconnect</button>
          </div>
        ) : (
          <div className="space-y-10 animate-in fade-in duration-1000">
            {/* Featured / TOP STORY (Hero) */}
            {breakingNews && (
              <TiltCard
                className="group relative h-[350px] md:h-[450px] w-full rounded-[2.5rem] overflow-hidden cursor-pointer gold-shiny-border"
                glowColor="rgba(255, 255, 255, 0.03)"
                onClick={() => handleNewsClick(breakingNews.url)}
              >
                {/* Background Image with optimized overlay */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${breakingNews.image || 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&q=80&w=1000'})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-transparent opacity-60" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 p-6 md:p-12 w-full md:w-4/5 z-10">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="px-3 py-1 bg-white text-black text-[10px] font-black rounded-full uppercase tracking-[0.1em] flex items-center gap-1.5 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                      <TrendingUp size={12} strokeWidth={3} /> BREAKING
                    </span>
                    <span className="flex items-center gap-1 text-slate-300 text-[11px] font-bold tracking-wider">
                      <Clock size={12} className="text-slate-400" /> {breakingNews.time}
                    </span>
                  </div>
                  <h2 className="text-xl md:text-5xl font-black text-[#F8FAFC] mb-4 leading-tight group-hover:text-white transition-colors duration-300">
                    {breakingNews.title}
                  </h2>
                  <p className="text-[#94A3B8] text-sm md:text-base font-medium line-clamp-2 max-w-3xl mb-6 opacity-80 leading-relaxed">
                    {breakingNews.description || "In-depth analysis of structural shifts across global markets and domestic policy vectors."}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1.5 rounded-xl">
                      <div className="w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center text-white font-black text-[10px]">
                        {breakingNews.source?.charAt(0)}
                      </div>
                      <span className="text-[12px] font-bold text-white tracking-wide">{breakingNews.source}</span>
                    </div>
                    <span className="text-white text-sm font-black uppercase tracking-widest flex items-center gap-2 hover:underline">
                      Full Intelligence <ExternalLink size={14} />
                    </span>
                  </div>
                </div>
              </TiltCard>
            )}

            {/* List Layout (The Grid) */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1">
              {otherNews.map((item, index) => (
                <TiltCard
                  key={index}
                  depth={3}
                  glowColor="rgba(255, 255, 255, 0.02)"
                  className="group bg-black rounded-[2rem] overflow-hidden cursor-pointer gold-shiny-border p-1 md:p-0"
                  onClick={() => handleNewsClick(item.url)}
                >
                  <div className="flex flex-col lg:flex-row h-full">
                    {/* Thumbnail */}
                    <div className="lg:w-72 h-44 lg:h-auto flex-shrink-0 relative overflow-hidden m-4 rounded-2xl bg-black">
                      <img
                        src={item.image || `https://images.unsplash.com/photo-1611974765270-ca12588265b6?auto=format&fit=crop&q=80&w=400&h=300`}
                        alt="Headline visual"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 contrast-[1.1] saturate-[1.2] brightness-[0.9] group-hover:brightness-105"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611974765270-ca12588265b6?auto=format&fit=crop&q=80&w=300&h=200'; }}
                      />
                      {/* Enhancement Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-white/5 opacity-60 pointer-events-none" />
                      <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl pointer-events-none" />
                    </div>

                    {/* Content */}
                    <div className="p-6 lg:p-10 flex flex-col justify-between flex-1">
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-slate-800 border border-white/10 flex items-center justify-center text-white font-black text-[10px]">
                              {item.source?.charAt(0)}
                            </div>
                            <span className="text-[12px] font-black text-[#94A3B8] uppercase tracking-widest">{item.source}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span className="text-[12px] font-medium text-[#94A3B8]/60">{item.time}</span>
                          </div>
                          <ExternalLink size={16} className="text-white opacity-0 group-hover:opacity-100 transition-all transform translate-x-1 group-hover:translate-x-0" />
                        </div>

                        <h3 className="text-lg md:text-xl font-semibold text-[#F8FAFC] leading-snug mb-3 group-hover:text-white transition-colors line-clamp-2">
                          {item.title}
                        </h3>

                        <p className="text-[#94A3B8] text-[13px] line-clamp-2 font-medium leading-relaxed opacity-70">
                          {item.description || "Critical market data and macroeconomic developments impacting current investment strategies."}
                        </p>
                      </div>

                      <div className="mt-6 flex items-center gap-4 pt-4 border-t border-white/5">
                        <span className="text-white text-[14px] font-black uppercase tracking-wider flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          Read Full Article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default News;